/**
 * Facturation API Client
 * All billing-related API calls
 */

import { z } from 'zod';
import { apiClient } from '@/core/api/client';
import type {
  Product,
  AffaireProduct,
  Invoice,
  CreditNote,
  BillingSummary,
  BillingContact,
  EligibleAffaire,
  EligibilityResult,
  PaginatedResponse,
  InvoiceFilters,
  CreditNoteFilters,
  CreateProductData,
  AddAffaireProductData,
  CreateCreditNoteData,
  VatValidation,
  PeppolRegistration,
  SequenceStatus,
  FalcoConfigStatus,
  Devis,
  DevisFilters,
  DevisPaginatedResponse,
  CreateDevisData,
  AddDevisLineData,
  ContactSearchResult,
  // Tarif types
  Tarif,
  TarifBanque,
  TarifService,
  TarifPrestation,
  TarifFilters,
  AffaireContext,
  TarifImportBatch,
  TarifImportResult,
  BanqueProvinces,
  // Custom invoice types
  CreateCustomInvoiceData,
  // Bank account types
  BankAccount,
  // Mixed-payer billing tracks
  AffaireMonthlyTrack
} from '../types/facturation.types';

const BASE_URL = '/api/billing';

// API Response wrapper types
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

// =============================================================================
// ZOD SCHEMAS FOR RUNTIME VALIDATION
// =============================================================================

// Product schema
const ProductSchema = z.object({
  id: z.number(),
  product_code: z.string(),
  description: z.string(),
  unit_price_htva: z.coerce.number(),
  tva_rate: z.coerce.number(),
  is_active: z.boolean(),
  honoraire_expert_htva: z.coerce.number().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

// Product invoice detail schema
const ProductInvoiceDetailSchema = z.object({
  invoice_number: z.string(),
  status: z.enum(['DRAFT', 'GENERATED', 'SENT', 'PAID', 'CANCELLED']),
  quantity: z.coerce.number(),
  amount_htva: z.coerce.number(),
  // Enriched fields (optional — only present when returned by the enriched
  // getAffaireProducts query, not by the legacy eligibility flow)
  invoice_id: z.number().optional(),
  invoice_type: z.enum(['SINGLE', 'MONTHLY', 'CUSTOM']).optional(),
  invoice_date: z.string().nullable().optional(),
  billing_name: z.string().nullable().optional(),
});

// Affaire product schema
const AffaireProductSchema = z.object({
  id: z.number(),
  uuid: z.string(),
  affaire_id: z.string(),
  product_id: z.number().nullable(),
  product_code: z.string(),
  description: z.string(),
  quantity: z.coerce.number(),
  unit_price_htva: z.coerce.number(),
  unit_price_tvac: z.coerce.number().optional(),  // TVAC source of truth for exact calculations
  tva_rate: z.coerce.number(),
  // Tarif reference - links to pricing matrix
  tarif_id: z.number().nullish(),
  banque_code: z.string().nullish(),  // Remembered for subsequent products
  service_code: z.string().nullish(), // Remembered for subsequent products
  // Partial billing tracking (0-100%), defaults to 0
  billed_percentage: z.coerce.number().optional(),
  // Reduction fields
  reduction_type: z.enum(['percentage', 'fixed']).nullish(),
  reduction_value: z.coerce.number().nullish(),
  // Expert fee
  honoraire_expert_htva: z.coerce.number().nullish(),
  created_by: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  // Calculated fields (optional)
  line_total_htva: z.coerce.number().optional(),
  line_total_tva: z.coerce.number().optional(),
  line_total_tvac: z.coerce.number().optional(),
  reduction_amount_htva: z.coerce.number().optional(),
  net_unit_price_htva: z.coerce.number().optional(),
  // Joined fields (optional, can be null from LEFT JOIN)
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  // Invoiced amounts (optional)
  original_quantity: z.coerce.number().optional(),
  original_htva: z.coerce.number().optional(),
  invoiced_quantity: z.coerce.number().optional(),
  invoiced_htva: z.coerce.number().optional(),
  remaining_quantity: z.coerce.number().optional(),
  remaining_htva: z.coerce.number().optional(),
  invoiced_percentage: z.coerce.number().optional(),
  fully_invoiced: z.boolean().optional(),
  // Invoice details (optional)
  invoices: z.array(ProductInvoiceDetailSchema).optional(),
  // Mixed-payer enrichment from getAffaireProducts query (optional)
  payer_track: z.enum(['CUSTOMER', 'BANK_GROUPED']).optional(),
  tarif_banque_code: z.string().nullish(),
  tarif_banque_name: z.string().nullish(),
  tarif_service_code: z.string().nullish(),
  tarif_service_name: z.string().nullish(),
  computed_billed_percentage: z.coerce.number().optional(),
  credited_quantity: z.coerce.number().optional(),
  credited_htva: z.coerce.number().optional(),
  queued_in_monthly_draft: z.boolean().optional(),
});

// Invoice line schema
const InvoiceLineSchema = z.object({
  id: z.number(),
  facture_id: z.number(),
  affaire_id: z.string(),
  affaire_product_uuid: z.string().nullable(),
  product_code: z.string().nullable(),
  description: z.string(),
  quantity: z.coerce.number(),
  unit_price_htva: z.coerce.number(),
  unit_price_tvac: z.coerce.number().optional(),  // TVAC price - source of truth for exact credit note calculations
  tva_rate: z.coerce.number(),
  line_total_htva: z.coerce.number(),
  line_total_tva: z.coerce.number(),
  line_total_tvac: z.coerce.number(),
  sort_order: z.number(),
  created_at: z.string(),
  // Credit tracking fields (from getInvoiceById)
  credited_quantity: z.coerce.number().optional(),
  credited_htva: z.coerce.number().optional(),
});

// Invoice affaire schema
const InvoiceAffaireSchema = z.object({
  affaire_id: z.string(),
  k_id_dossier: z.string().nullish(),
  adresse_bien: z.string().nullish(),
});

// Invoice schema
const InvoiceSchema = z.object({
  id: z.number(),
  invoice_number: z.string().nullable(),  // Null for DRAFT invoices (number assigned at validation)
  invoice_type: z.enum(['SINGLE', 'MONTHLY', 'CUSTOM']),
  status: z.enum(['DRAFT', 'GENERATED', 'SENT', 'PAID', 'CANCELLED']),
  billing_contact_id: z.string().nullable(),
  billing_name: z.string(),
  billing_address: z.string().nullable(),
  billing_vat_number: z.string().nullable(),
  billing_email: z.string().nullable(),
  total_htva: z.coerce.number(),
  total_tva: z.coerce.number(),
  total_tvac: z.coerce.number(),
  invoice_date: z.string(),
  due_date: z.string().nullable(),
  pdf_path: z.string().nullable(),
  pdf_generated_at: z.string().nullable(),
  structured_communication: z.string().nullable(),
  language: z.string(),
  falco_document_id: z.string().nullable(),
  falco_status: z.enum(['PENDING', 'UPLOADED', 'PROCESSING', 'SENT', 'FAILED']).nullable(),
  falco_synced_at: z.string().nullable(),
  falco_error: z.string().nullable(),
  falco_retry_count: z.number(),
  peppol_status: z.enum(['PENDING', 'DELIVERED', 'FAILED', 'NOT_REGISTERED']).nullable(),
  peppol_delivery_id: z.string().nullable(),
  peppol_delivered_at: z.string().nullable(),
  peppol_error: z.string().nullable(),
  notes: z.string().nullable(),
  created_by: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  // Validation tracking
  validated_by: z.string().nullable().optional(),
  validated_at: z.string().nullable().optional(),
  // Joined fields (optional, can be null from LEFT JOIN)
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  validator_first_name: z.string().nullish(),
  validator_last_name: z.string().nullish(),
  lines: z.array(InvoiceLineSchema).optional(),
  affaires: z.array(InvoiceAffaireSchema).optional(),
  // Email tracking
  email_sent: z.boolean().optional(),
  email_sent_at: z.string().nullable().optional(),
  email_recipient: z.string().nullable().optional(),
  email_error: z.string().nullable().optional(),
  // Credit note tracking
  is_fully_credited: z.boolean().optional(),
  total_credited: z.coerce.number().optional(),
  // Test mode
  is_test: z.boolean().optional(),
});

// Credit note line schema
const CreditNoteLineSchema = z.object({
  id: z.number(),
  note_credit_id: z.number().optional(),  // Not included in nested response
  original_ligne_id: z.number().nullable().optional(),
  affaire_product_uuid: z.string().nullable().optional(),
  description: z.string(),
  quantity: z.coerce.number(),
  unit_price_htva: z.coerce.number(),
  tva_rate: z.coerce.number(),
  line_total_htva: z.coerce.number(),
  line_total_tva: z.coerce.number(),
  line_total_tvac: z.coerce.number(),
  created_at: z.string().optional(),  // Not included in nested response
});

// Credit note invoice schema
const CreditNoteInvoiceSchema = z.object({
  id: z.number(),
  note_credit_id: z.number(),
  facture_id: z.number(),
  invoice_number: z.string().nullish(),
  affaire_id: z.string().nullish(),
  k_id_dossier: z.string().nullish(),
});

// Credit note schema
const CreditNoteSchema = z.object({
  id: z.number(),
  credit_note_number: z.string().nullable(),  // Null for DRAFT credit notes (number assigned at validation)
  original_facture_id: z.number().nullish(),
  credit_type: z.enum(['FULL', 'PARTIAL', 'CUSTOM']),
  status: z.enum(['DRAFT', 'GENERATED', 'SENT', 'CANCELLED']),
  total_htva: z.coerce.number(),
  total_tva: z.coerce.number(),
  total_tvac: z.coerce.number(),
  reason: z.string(),
  pdf_path: z.string().nullable(),
  pdf_generated_at: z.string().nullable(),
  falco_document_id: z.string().nullable(),
  falco_status: z.enum(['PENDING', 'UPLOADED', 'PROCESSING', 'SENT', 'FAILED']).nullable(),
  falco_synced_at: z.string().nullable(),
  falco_error: z.string().nullable(),
  peppol_status: z.enum(['PENDING', 'DELIVERED', 'FAILED', 'NOT_REGISTERED']).nullable(),
  peppol_delivery_id: z.string().nullable(),
  peppol_delivered_at: z.string().nullable(),
  peppol_error: z.string().nullable(),
  created_by: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  // Validation tracking
  validated_by: z.string().nullable().optional(),
  validated_at: z.string().nullable().optional(),
  // Email tracking (same as invoice)
  email_sent: z.boolean().optional(),
  email_sent_at: z.string().nullable().optional(),
  email_recipient: z.string().nullable().optional(),
  email_error: z.string().nullable().optional(),
  // Custom credit note fields
  is_custom: z.boolean().optional(),
  title: z.string().nullable().optional(),
  billing_email: z.string().nullable().optional(),
  language: z.string().optional(),
  // Joined fields (optional, can be null from LEFT JOIN)
  original_invoice_number: z.string().nullish(),
  billing_name: z.string().nullish(),
  billing_address: z.string().nullish(),
  billing_vat_number: z.string().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  validator_first_name: z.string().nullish(),
  validator_last_name: z.string().nullish(),
  lines: z.array(CreditNoteLineSchema).optional(),
  invoices: z.array(CreditNoteInvoiceSchema).optional(),
});

// Devis line schema
const DevisLineSchema = z.object({
  id: z.number(),
  devis_id: z.number(),
  product_id: z.number().nullable(),
  product_code: z.string().nullable(),
  description: z.string(),
  quantity: z.coerce.number(),
  unit_price_htva: z.coerce.number(),
  tva_rate: z.coerce.number(),
  line_total_htva: z.coerce.number(),
  line_total_tva: z.coerce.number(),
  line_total_tvac: z.coerce.number(),
  sort_order: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});

// Devis schema
const DevisSchema = z.object({
  id: z.number(),
  devis_number: z.string(),
  status: z.enum(['DRAFT', 'SENT']),
  contact_id: z.string().nullable(),
  contact_name: z.string(),
  contact_address: z.string().nullable(),
  contact_vat_number: z.string().nullable(),
  contact_email: z.string().nullable(),
  contact_phone: z.string().nullable(),
  is_custom_contact: z.boolean(),
  total_htva: z.coerce.number(),
  total_tva: z.coerce.number(),
  total_tvac: z.coerce.number(),
  devis_date: z.string(),
  validity_date: z.string().nullable(),
  pdf_path: z.string().nullable(),
  pdf_generated_at: z.string().nullable(),
  linked_affaire_id: z.string().nullable(),
  converted_at: z.string().nullable(),
  language: z.string(),
  notes: z.string().nullable(),
  created_by: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  lines: z.array(DevisLineSchema).optional(),
});

// Response schemas
const ApiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema,
  });

const PaginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    success: z.boolean(),
    data: z.array(itemSchema).optional(),
    invoices: z.array(itemSchema).optional(),
    creditNotes: z.array(itemSchema).optional(),
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    totalPages: z.number(),
  });

const DevisPaginatedResponseSchema = z.object({
  success: z.boolean(),
  devis: z.array(DevisSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});

// Helper function to validate API responses
// IMPORTANT: Throws error on validation failure instead of returning invalid data
// This prevents subtle bugs where error objects are treated as valid responses
function validateResponse<T>(
  data: unknown,
  schema: z.ZodSchema<T>,
  endpoint: string
): T {
  const result = schema.safeParse(data);

  if (!result.success) {
    console.error(`[API Validation Error] ${endpoint}:`, {
      errors: result.error.issues,
      receivedData: data,
    });
    // Throw error with useful context instead of silently returning invalid data
    const firstError = result.error.issues[0];
    throw new Error(
      `Invalid API response from ${endpoint}: ${firstError?.path?.join('.') || 'root'} - ${firstError?.message || 'validation failed'}`
    );
  }

  return result.data;
}

// =============================================================================
// PRODUCTS
// =============================================================================

export const getProducts = async (activeOnly = true): Promise<Product[]> => {
  const response = await apiClient.get<ApiResponse<Product[]>>(`${BASE_URL}/products`, {
    params: { active: activeOnly }
  });

  const validated = validateResponse(
    response,
    ApiResponseSchema(z.array(ProductSchema)),
    'GET /products'
  );

  return validated.data;
};

export const getProductById = async (id: number): Promise<Product> => {
  const response = await apiClient.get<ApiResponse<Product>>(`${BASE_URL}/products/${id}`);

  const validated = validateResponse(
    response,
    ApiResponseSchema(ProductSchema),
    `GET /products/${id}`
  );

  return validated.data;
};

export const createProduct = async (data: CreateProductData): Promise<Product> => {
  const response = await apiClient.post<ApiResponse<Product>>(`${BASE_URL}/products`, data);

  const validated = validateResponse(
    response,
    ApiResponseSchema(ProductSchema),
    'POST /products'
  );

  return validated.data;
};

export const updateProduct = async (id: number, data: Partial<CreateProductData>): Promise<Product> => {
  const response = await apiClient.put<ApiResponse<Product>>(`${BASE_URL}/products/${id}`, data);

  const validated = validateResponse(
    response,
    ApiResponseSchema(ProductSchema),
    `PUT /products/${id}`
  );

  return validated.data;
};

export const deleteProduct = async (id: number): Promise<void> => {
  await apiClient.delete(`${BASE_URL}/products/${id}`);
};

// =============================================================================
// TARIFS - Pricing Matrix (imported from Excel)
// =============================================================================

// Tarif schema
const TarifSchema = z.object({
  id: z.number(),
  banque_code: z.string(),
  banque_name: z.string(),
  service_code: z.string(),
  service_name: z.string(),
  prestation_code: z.string(),
  prestation_name: z.string(),
  price_htva: z.coerce.number(),
  price_tvac: z.coerce.number(),
  honoraire_expert_htva: z.coerce.number().nullable(),
  tva_rate: z.coerce.number(),
  sort_order: z.number(),
  is_active: z.boolean(),
  import_batch_id: z.string().nullish(),
  available_provinces: z.array(z.string()).default([]),
  created_at: z.string(),
  updated_at: z.string(),
});

// Tarif banque schema (distinct banques)
const TarifBanqueSchema = z.object({
  banque_code: z.string(),
  banque_name: z.string(),
  sort_order: z.number(),
});

// Tarif service schema (distinct services)
const TarifServiceSchema = z.object({
  service_code: z.string(),
  service_name: z.string(),
});

// Tarif prestation schema (for selection dropdown)
const TarifPrestationSchema = z.object({
  id: z.number(),
  prestation_code: z.string(),
  prestation_name: z.string(),
  price_htva: z.coerce.number(),
  price_tvac: z.coerce.number(),
  honoraire_expert_htva: z.coerce.number().nullable(),
  tva_rate: z.coerce.number(),
  sort_order: z.number(),
  available_provinces: z.array(z.string()).default([]),
});

// Affaire context schema (remembered banque/service)
const AffaireContextSchema = z.object({
  banque_code: z.string(),
  service_code: z.string(),
});

// Import batch schema
const TarifImportBatchSchema = z.object({
  import_batch_id: z.string(),
  tarif_count: z.coerce.number(),
  imported_at: z.string(),
  banques: z.array(z.string()),
});

// Import result schema
const TarifImportResultSchema = z.object({
  batchId: z.string(),
  totalImported: z.number(),
  errorCount: z.number(),
  errors: z.array(z.object({
    sheet: z.string(),
    prestation: z.string(),
    service: z.string(),
    error: z.string(),
  })),
});

/**
 * Get all tarifs with optional filters
 */
export const getTarifs = async (filters: TarifFilters = {}): Promise<Tarif[]> => {
  const response = await apiClient.get<ApiResponse<Tarif[]>>(`${BASE_URL}/tarifs`, {
    params: {
      banqueCode: filters.banqueCode,
      serviceCode: filters.serviceCode,
      active: filters.activeOnly !== false,
    }
  });

  const validated = validateResponse(
    response,
    ApiResponseSchema(z.array(TarifSchema)),
    'GET /tarifs'
  );

  return validated.data;
};

/**
 * Get tarif by ID
 */
export const getTarifById = async (id: number): Promise<Tarif> => {
  const response = await apiClient.get<ApiResponse<Tarif>>(`${BASE_URL}/tarifs/${id}`);

  const validated = validateResponse(
    response,
    ApiResponseSchema(TarifSchema),
    `GET /tarifs/${id}`
  );

  return validated.data;
};

/**
 * Get distinct banques from tarifs
 */
export const getTarifBanques = async (): Promise<TarifBanque[]> => {
  const response = await apiClient.get<ApiResponse<TarifBanque[]>>(`${BASE_URL}/tarifs/banques`);

  const validated = validateResponse(
    response,
    ApiResponseSchema(z.array(TarifBanqueSchema)),
    'GET /tarifs/banques'
  );

  return validated.data;
};

/**
 * Get distinct services from tarifs
 */
export const getTarifServices = async (): Promise<TarifService[]> => {
  const response = await apiClient.get<ApiResponse<TarifService[]>>(`${BASE_URL}/tarifs/services`);

  const validated = validateResponse(
    response,
    ApiResponseSchema(z.array(TarifServiceSchema)),
    'GET /tarifs/services'
  );

  return validated.data;
};

/**
 * Get prestations for a specific banque and service
 */
export const getTarifPrestations = async (
  banqueCode: string,
  serviceCode: string
): Promise<TarifPrestation[]> => {
  const response = await apiClient.get<ApiResponse<TarifPrestation[]>>(`${BASE_URL}/tarifs/prestations`, {
    params: { banqueCode, serviceCode }
  });

  const validated = validateResponse(
    response,
    ApiResponseSchema(z.array(TarifPrestationSchema)),
    'GET /tarifs/prestations'
  );

  return validated.data;
};

/**
 * Get bank-level province defaults (the "RÈGLE BANQUE" Excel row)
 * - no arg: returns all banks
 * - banqueCode: returns the single bank or null
 */
const BanqueProvincesSchema = z.object({
  banque_code: z.string(),
  banque_name: z.string(),
  available_provinces: z.array(z.string()).default([]),
  updated_at: z.string(),
});

export const getBanqueProvinces = async (): Promise<BanqueProvinces[]> => {
  const response = await apiClient.get<ApiResponse<BanqueProvinces[]>>(
    `${BASE_URL}/tarifs/banque-provinces`
  );
  const validated = validateResponse(
    response,
    ApiResponseSchema(z.array(BanqueProvincesSchema)),
    'GET /tarifs/banque-provinces'
  );
  return validated.data;
};

export const getBanqueProvincesByCode = async (banqueCode: string): Promise<BanqueProvinces | null> => {
  const response = await apiClient.get<ApiResponse<BanqueProvinces | null>>(
    `${BASE_URL}/tarifs/banque-provinces/${banqueCode}`
  );
  if (!response.data) return null;
  const validated = validateResponse(
    response,
    ApiResponseSchema(BanqueProvincesSchema.nullable()),
    `GET /tarifs/banque-provinces/${banqueCode}`
  );
  return validated.data;
};

/**
 * Get remembered banque/service for an affaire
 */
export const getAffaireContext = async (affaireId: string): Promise<AffaireContext | null> => {
  const response = await apiClient.get<ApiResponse<AffaireContext | null>>(
    `${BASE_URL}/tarifs/affaire/${affaireId}/context`
  );

  if (!response.data) return null;

  const validated = validateResponse(
    response,
    ApiResponseSchema(AffaireContextSchema.nullable()),
    `GET /tarifs/affaire/${affaireId}/context`
  );

  return validated.data;
};

/**
 * Upload tarifs from Excel file
 */
export const uploadTarifsExcel = async (file: File): Promise<TarifImportResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<ApiResponse<TarifImportResult>>(
    `${BASE_URL}/tarifs/import`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  const validated = validateResponse(
    response,
    ApiResponseSchema(TarifImportResultSchema),
    'POST /tarifs/import'
  );

  return validated.data;
};

/**
 * Get import batches
 */
export const getTarifImportBatches = async (): Promise<TarifImportBatch[]> => {
  const response = await apiClient.get<ApiResponse<TarifImportBatch[]>>(`${BASE_URL}/tarifs/batches`);

  const validated = validateResponse(
    response,
    ApiResponseSchema(z.array(TarifImportBatchSchema)),
    'GET /tarifs/batches'
  );

  return validated.data;
};

/**
 * Delete tarifs by batch ID
 */
export const deleteTarifBatch = async (batchId: string): Promise<{ deletedCount: number }> => {
  const response = await apiClient.delete<ApiResponse<{ deletedCount: number }>>(
    `${BASE_URL}/tarifs/batch/${batchId}`
  );
  return response.data;
};

/**
 * Deactivate all tarifs (before new import)
 */
export const deactivateAllTarifs = async (): Promise<{ deactivatedCount: number }> => {
  const response = await apiClient.post<ApiResponse<{ deactivatedCount: number }>>(
    `${BASE_URL}/tarifs/deactivate-all`
  );
  return response.data;
};

// =============================================================================
// AFFAIRE PRODUCTS
// =============================================================================

export const getAffaireProducts = async (affaireId: string): Promise<AffaireProduct[]> => {
  const response = await apiClient.get<ApiResponse<AffaireProduct[]>>(`${BASE_URL}/affaires/${affaireId}/products`);

  const validated = validateResponse(
    response,
    ApiResponseSchema(z.array(AffaireProductSchema)),
    `GET /affaires/${affaireId}/products`
  );

  return validated.data;
};

export const addAffaireProduct = async (affaireId: string, data: AddAffaireProductData): Promise<AffaireProduct> => {
  const response = await apiClient.post<ApiResponse<AffaireProduct>>(`${BASE_URL}/affaires/${affaireId}/products`, data);

  const validated = validateResponse(
    response,
    ApiResponseSchema(AffaireProductSchema),
    `POST /affaires/${affaireId}/products`
  );

  return validated.data;
};

export const updateAffaireProduct = async (affaireId: string, productId: number, data: Partial<AddAffaireProductData>): Promise<AffaireProduct> => {
  const response = await apiClient.put<ApiResponse<AffaireProduct>>(`${BASE_URL}/affaires/${affaireId}/products/${productId}`, data);

  const validated = validateResponse(
    response,
    ApiResponseSchema(AffaireProductSchema),
    `PUT /affaires/${affaireId}/products/${productId}`
  );

  return validated.data;
};

export const removeAffaireProduct = async (affaireId: string, productId: number): Promise<void> => {
  await apiClient.delete(`${BASE_URL}/affaires/${affaireId}/products/${productId}`);
};

export const getAffaireBillingSummary = async (affaireId: string): Promise<BillingSummary> => {
  const response = await apiClient.get<ApiResponse<BillingSummary>>(`${BASE_URL}/affaires/${affaireId}/summary`);
  return response.data;
};

export const getAffaireBillingContact = async (affaireId: string): Promise<BillingContact> => {
  const response = await apiClient.get<ApiResponse<BillingContact>>(`${BASE_URL}/affaires/${affaireId}/billing-contact`);
  return response.data;
};

/**
 * Get ALL billing contacts for an affaire (for selection when multiple FACTURE contacts exist)
 */
export const getAffaireBillingContacts = async (affaireId: string): Promise<BillingContact[]> => {
  const response = await apiClient.get<ApiResponse<BillingContact[]>>(`${BASE_URL}/affaires/${affaireId}/billing-contacts`);
  return response.data;
};

/**
 * Update a contact's email address
 */
export const updateContactEmail = async (contactId: string, email: string): Promise<BillingContact> => {
  const response = await apiClient.put<ApiResponse<BillingContact>>(`${BASE_URL}/contacts/${contactId}/email`, { email });
  return response.data;
};

// =============================================================================
// PAYMENT SUMMARY - For CaseSidebar display
// =============================================================================

/**
 * Payment status type for color-coded display
 */
export type PaymentStatusType = 'no_products' | 'a_facturer' | 'a_facturer_partiel' | 'en_attente' | 'paye_partiel' | 'paye';

/**
 * Payment summary response for sidebar display
 */
export interface PaymentSummaryResponse {
  billing: {
    totalHtva: number;
    totalTvac: number;
    expertHonorairesTvac: number;
    productCount: number;
    unbilledProductCount: number;
    affaireMontantTvac?: number; // Fallback amount from fm_affaires when no products exist
  };
  invoicing: {
    hasInvoice: boolean;
    invoiceCount: number;
    totalInvoicedTvac: number;
    latestInvoiceNumber: string | null;
  };
  payments: {
    totalPaid: number;
    paymentStatus: PaymentStatusType;
    isDirectPayment?: boolean; // No products but payment matches affaire amount
  };
}

/**
 * Get complete payment/billing status for an affaire (used in CaseSidebar)
 * Returns billing totals, invoice status, and payment status with color coding
 */
export const getAffairePaymentSummary = async (affaireId: string): Promise<PaymentSummaryResponse> => {
  const response = await apiClient.get<ApiResponse<PaymentSummaryResponse>>(`${BASE_URL}/affaires/${affaireId}/payment-summary`);
  return response.data;
};

// =============================================================================
// INVOICES
// =============================================================================

export const getInvoices = async (filters: InvoiceFilters = {}): Promise<PaginatedResponse<Invoice>> => {
  const response = await apiClient.get<PaginatedResponse<Invoice>>(`${BASE_URL}/invoices`, { params: filters });

  const validated = validateResponse(
    response,
    PaginatedResponseSchema(InvoiceSchema),
    'GET /invoices'
  );

  return validated as PaginatedResponse<Invoice>;
};

export const getInvoiceById = async (id: number): Promise<Invoice> => {
  const response = await apiClient.get<ApiResponse<Invoice>>(`${BASE_URL}/invoices/${id}`);

  const validated = validateResponse(
    response,
    ApiResponseSchema(InvoiceSchema),
    `GET /invoices/${id}`
  );

  return validated.data;
};

export const downloadInvoicePdf = async (id: number): Promise<Blob> => {
  const response = await apiClient.get<Blob>(`${BASE_URL}/invoices/${id}/pdf`, {
    responseType: 'blob'
  });
  return response;
};

/**
 * Preview a draft invoice PDF with "BROUILLON" watermark
 * Returns a PDF blob for streaming/display without saving to disk
 */
export const previewInvoicePdf = async (id: number): Promise<Blob> => {
  const response = await apiClient.get<Blob>(`${BASE_URL}/invoices/${id}/preview-pdf`, {
    responseType: 'blob'
  });
  return response;
};

export const checkSingleInvoiceEligibility = async (affaireId: string): Promise<EligibilityResult> => {
  const response = await apiClient.get<ApiResponse<EligibilityResult>>(`${BASE_URL}/invoices/single/eligibility/${affaireId}`);
  return response.data;
};

export const generateSingleInvoice = async (
  affaireId: string,
  options?: {
    language?: string;
    billingContactId?: string;  // Optional: specific contact when multiple FACTURE contacts exist
    selectedProductIds?: number[];  // Legacy: simple product selection
    productSelections?: Array<{ productId: number; quantity: number }>;  // New: with custom quantities
    discount?: {
      code: string;
      type: 'percentage' | 'fixed';
      value: number;
      description?: string;
    };
    viesOfficialData?: {  // VIES official data for professional customers
      name?: string | null;
      address?: string | null;
    };
    bankAccountId?: number;
    billingAddressOverride?: {  // Operator-edited billing address (overrides FM/VIES)
      address?: string | null;
      postalCode?: string | null;
      city?: string | null;
      countryCode?: string | null;
    };
    reverseCharge?: boolean;  // Intra-EU reverse charge (autoliquidation, 0% VAT); false to force off
  }
): Promise<Invoice> => {
  const response = await apiClient.post<ApiResponse<Invoice>>(`${BASE_URL}/invoices/single`, {
    affaireId,
    language: options?.language,
    billingContactId: options?.billingContactId,
    selectedProductIds: options?.selectedProductIds,
    productSelections: options?.productSelections,
    discount: options?.discount,
    viesOfficialData: options?.viesOfficialData,
    bankAccountId: options?.bankAccountId,
    billingAddressOverride: options?.billingAddressOverride,
    reverseCharge: options?.reverseCharge
  });

  const validated = validateResponse(
    response,
    ApiResponseSchema(InvoiceSchema),
    'POST /invoices/single'
  );

  return validated.data;
};

export const getMonthlyBillingEligible = async (billingContactId: string): Promise<EligibleAffaire[]> => {
  const response = await apiClient.get<ApiResponse<EligibleAffaire[]>>(`${BASE_URL}/invoices/monthly/eligible`, {
    params: { billingContactId }
  });
  return response.data;
};

/**
 * Get the FACTURE MENSUELLE billing track(s) attached to a single affaire.
 * Returns [] when the affaire has no FACTURE MENSUELLE contact (pure customer billing).
 */
export const getAffaireMonthlyBillingTracks = async (
  affaireId: string
): Promise<AffaireMonthlyTrack[]> => {
  const response = await apiClient.get<ApiResponse<AffaireMonthlyTrack[]>>(
    `${BASE_URL}/affaires/${affaireId}/monthly-billing-tracks`
  );
  return response.data;
};

/**
 * Monthly billing contact for group facturation
 */
export interface MonthlyBillingContact {
  contact_id: string;
  contact_name: string;
  company: string | null;
  vat_number: string | null;
  email: string | null;
  address: string | null;
  langue_communication: string | null;
  pays: string | null;  // Country code for VAT (e.g., "LU", "FR")
  affaire_count: number;
}

/**
 * Get all distinct billing contacts with FACTURE MENSUELLE role
 * that have eligible affaires for grouped invoicing.
 * Used by the Group Facturation tab.
 */
export const getMonthlyBillingContacts = async (): Promise<MonthlyBillingContact[]> => {
  const response = await apiClient.get<ApiResponse<MonthlyBillingContact[]>>(`${BASE_URL}/invoices/monthly/contacts`);
  return response.data;
};

/**
 * Get all eligible affaires for a CLIENT, grouped by billing contact
 * This enables multi-affaire invoicing by client selection rather than just billing contact
 */
export const getClientBillingEligible = async (clientId: string): Promise<{
  client_id: string;
  billing_contacts: Array<{
    billing_contact_id: string;
    billing_contact_name: string;
    billing_contact_role: string;
    billing_contact_vat?: string | null;
    billing_contact_address?: string | null;
    billing_contact_email?: string | null;
    billing_contact_pays?: string | null;
    affaires: EligibleAffaire[];
    totals: { htva: number; tva: number; tvac: number };
  }>;
  total_affaires: number;
}> => {
  const response = await apiClient.get<ApiResponse<{
    client_id: string;
    billing_contacts: Array<{
      billing_contact_id: string;
      billing_contact_name: string;
      billing_contact_role: string;
      billing_contact_vat?: string | null;
      billing_contact_address?: string | null;
      billing_contact_email?: string | null;
      billing_contact_pays?: string | null;
      affaires: EligibleAffaire[];
      totals: { htva: number; tva: number; tvac: number };
    }>;
    total_affaires: number;
  }>>(`${BASE_URL}/invoices/client-eligible`, {
    params: { clientId }
  });
  return response.data;
};

export const generateMonthlyInvoice = async (
  affaireIds: string[],
  billingContactId: string,
  options?: {
    language?: string;
    viesOfficialData?: {  // VIES official data for professional customers
      name?: string | null;
      address?: string | null;
    };
    bankAccountId?: number;
  }
): Promise<Invoice> => {
  const response = await apiClient.post<ApiResponse<Invoice>>(`${BASE_URL}/invoices/monthly`, {
    affaireIds,
    billingContactId,
    language: options?.language,
    viesOfficialData: options?.viesOfficialData,
    bankAccountId: options?.bankAccountId
  });
  return response.data;
};

/**
 * Create a custom invoice (not linked to any affaire)
 * Used for one-time customers or services outside of normal affaire workflow
 */
export const createCustomInvoice = async (data: CreateCustomInvoiceData): Promise<Invoice> => {
  const response = await apiClient.post<ApiResponse<Invoice>>(`${BASE_URL}/invoices/custom`, data);

  const validated = validateResponse(
    response,
    ApiResponseSchema(InvoiceSchema),
    'POST /invoices/custom'
  );

  return validated.data;
};

/**
 * Data for creating a custom credit note (standalone, not linked to invoice)
 */
export interface CreateCustomCreditNoteData {
  customer: {
    name: string;
    address?: string;
    postalCode?: string;
    city?: string;
    countryCode?: string;
    email: string;
    vatNumber?: string;
  };
  reason: string;
  lines: Array<{
    description: string;
    quantity: number;
    unitPriceHtva: number;
    unitPriceTvac?: number;  // TVAC source of truth when the user entered a TVAC amount
    tvaRate: 0 | 6 | 12 | 21;
  }>;
  language?: 'fr' | 'nl' | 'de' | 'en';
  title?: string;
  affaireId?: string;  // Optional: link to affaire (for affaire-context credit notes)
  creditNoteDate?: string;  // Optional: custom date (YYYY-MM-DD) for the credit note, defaults to today
}

/**
 * Create a custom credit note (not linked to any invoice)
 * Used for standalone credit notes outside of normal invoice workflow
 */
export const createCustomCreditNote = async (data: CreateCustomCreditNoteData): Promise<CreditNote> => {
  const response = await apiClient.post<ApiResponse<CreditNote>>(`${BASE_URL}/credit-notes/custom`, data);

  const validated = validateResponse(
    response,
    ApiResponseSchema(CreditNoteSchema),
    'POST /credit-notes/custom'
  );

  return validated.data;
};

interface FinalizeResult {
  pdfPath: string;
  falco?: unknown;
  peppolStatus: string;
}

interface ValidateResult {
  success?: boolean;
  invoice?: Invoice;  // The validated invoice with updated status, invoice_number, pdf_path
  falco?: unknown;
  peppolStatus: string;
  emailSent?: boolean;
  emailRecipient?: string | null;
  emailError?: string | null;
}

export interface ValidateInvoiceOptions {
  sendEmail?: boolean;
  emailMessage?: string;
  emailRecipient?: string;
  language?: string;
}

// Email preview response
export interface EmailPreviewData {
  subject: string;
  body: string;
  language: string;
  recipient: string;
}

/**
 * Fetch the default email template for a given invoice and language.
 * Used to pre-fill the email editor in InvoiceDetailModal.
 */
export const getEmailPreview = async (
  invoiceId: number,
  language: string
): Promise<{ success: boolean; data: EmailPreviewData }> => {
  const response = await apiClient.get<{ success: boolean; data: EmailPreviewData }>(
    `${BASE_URL}/invoices/${invoiceId}/email-preview`,
    { params: { language } }
  );
  return response as unknown as { success: boolean; data: EmailPreviewData };
};

/**
 * @deprecated Use generate + validate workflow instead.
 * Generate now creates PDF immediately, validate uploads to Falco.
 */
export const finalizeInvoice = async (id: number): Promise<FinalizeResult> => {
  const response = await apiClient.post<ApiResponse<FinalizeResult>>(`${BASE_URL}/invoices/${id}/finalize`);
  return response.data;
};

/**
 * Validate an invoice - uploads to Falco/Horus and marks as SENT (locked)
 * This is step 2 of the new workflow:
 * 1. Generate invoice → PDF created → status GENERATED
 * 2. Validate invoice → Falco upload → status SENT (cannot delete)
 *
 * @param id Invoice ID
 * @param options Optional email options
 * @param options.sendEmail Whether to send the invoice by email
 * @param options.emailMessage Custom email message (optional)
 * @param options.emailRecipient Override recipient email (optional, for testing)
 */
export const validateInvoice = async (id: number, options?: ValidateInvoiceOptions): Promise<ValidateResult> => {
  const response = await apiClient.post<ApiResponse<ValidateResult>>(`${BASE_URL}/invoices/${id}/validate`, options || {});
  return response.data;
};

export const cancelInvoice = async (id: number, reason: string): Promise<void> => {
  await apiClient.post(`${BASE_URL}/invoices/${id}/cancel`, { reason });
};

export const markInvoiceAsPaid = async (id: number, paymentDate?: string, paymentReference?: string): Promise<void> => {
  await apiClient.post(`${BASE_URL}/invoices/${id}/mark-paid`, { paymentDate, paymentReference });
};

export const resyncInvoiceFalco = async (id: number): Promise<unknown> => {
  const response = await apiClient.post<ApiResponse<unknown>>(`${BASE_URL}/invoices/${id}/resync-falco`);
  return response.data;
};

export const getSequenceStatus = async (year?: number): Promise<SequenceStatus> => {
  const response = await apiClient.get<ApiResponse<SequenceStatus>>(`${BASE_URL}/invoices/sequence-status`, {
    params: year ? { year } : undefined
  });
  return response.data;
};

interface NextNumberResponse {
  nextNumber: string;
}

export const peekNextInvoiceNumber = async (year?: number, type: 'INVOICE' | 'CREDIT_NOTE' = 'INVOICE'): Promise<string> => {
  const response = await apiClient.get<ApiResponse<NextNumberResponse>>(`${BASE_URL}/invoices/next-number`, {
    params: { year, type }
  });
  return response.data.nextNumber;
};

// =============================================================================
// CREDIT NOTES
// =============================================================================

export const getCreditNotes = async (filters: CreditNoteFilters = {}): Promise<PaginatedResponse<CreditNote>> => {
  const response = await apiClient.get<PaginatedResponse<CreditNote>>(`${BASE_URL}/credit-notes`, { params: filters });

  const validated = validateResponse(
    response,
    PaginatedResponseSchema(CreditNoteSchema),
    'GET /credit-notes'
  );

  return validated as PaginatedResponse<CreditNote>;
};

export const getCreditNoteById = async (id: number): Promise<CreditNote> => {
  const response = await apiClient.get<ApiResponse<CreditNote>>(`${BASE_URL}/credit-notes/${id}`);

  const validated = validateResponse(
    response,
    ApiResponseSchema(CreditNoteSchema),
    `GET /credit-notes/${id}`
  );

  return validated.data;
};

export const downloadCreditNotePdf = async (id: number): Promise<Blob> => {
  const response = await apiClient.get<Blob>(`${BASE_URL}/credit-notes/${id}/pdf`, {
    responseType: 'blob'
  });
  return response;
};

export const createCreditNote = async (data: CreateCreditNoteData): Promise<CreditNote> => {
  const response = await apiClient.post<ApiResponse<CreditNote>>(`${BASE_URL}/credit-notes`, data);

  const validated = validateResponse(
    response,
    ApiResponseSchema(CreditNoteSchema),
    'POST /credit-notes'
  );

  return validated.data;
};

/**
 * @deprecated Use create + validate workflow instead
 */
export const finalizeCreditNote = async (id: number): Promise<FinalizeResult> => {
  const response = await apiClient.post<ApiResponse<FinalizeResult>>(`${BASE_URL}/credit-notes/${id}/finalize`);
  return response.data;
};

/**
 * Validate a credit note - assigns credit note number, generates PDF, uploads to Falco
 * This is step 2 of the new workflow:
 * 1. Create credit note → creates draft WITHOUT credit_note_number → status DRAFT
 * 2. Validate credit note → assigns number, generates PDF, Falco upload → status SENT
 */
export const validateCreditNote = async (id: number, sendEmail?: boolean): Promise<CreditNote> => {
  const response = await apiClient.post<ApiResponse<CreditNote>>(`${BASE_URL}/credit-notes/${id}/validate`, {
    sendEmail: sendEmail ?? true,
  });

  const validated = validateResponse(
    response,
    ApiResponseSchema(CreditNoteSchema),
    `POST /credit-notes/${id}/validate`
  );

  return validated.data;
};

/**
 * Preview a draft credit note PDF with "BROUILLON" watermark
 * Returns a PDF blob for streaming/display without saving to disk
 */
export const previewCreditNotePdf = async (id: number): Promise<Blob> => {
  const response = await apiClient.get<Blob>(`${BASE_URL}/credit-notes/${id}/preview-pdf`, {
    responseType: 'blob'
  });
  return response;
};

export const cancelCreditNote = async (id: number, reason: string): Promise<void> => {
  await apiClient.post(`${BASE_URL}/credit-notes/${id}/cancel`, { reason });
};

// =============================================================================
// VAT VALIDATION
// =============================================================================

export const validateVat = async (vatNumber: string, storedName?: string, storedAddress?: string): Promise<VatValidation> => {
  const response = await apiClient.post<ApiResponse<VatValidation>>(`${BASE_URL}/vat/validate`, {
    vatNumber,
    storedName,
    storedAddress
  });
  return response.data;
};

export const getVatValidationHistory = async (vatNumber: string, limit = 10): Promise<VatValidation[]> => {
  const response = await apiClient.get<ApiResponse<VatValidation[]>>(`${BASE_URL}/vat/history/${vatNumber}`, {
    params: { limit }
  });
  return response.data;
};

export const acceptVatValidation = async (validationId: number): Promise<void> => {
  await apiClient.post(`${BASE_URL}/vat/accept/${validationId}`);
};

interface IntraCommunityResult {
  isIntraCommunity: boolean;
  applicableVatRate: number;
}

export const checkIntraCommunity = async (senderVatNumber: string, receiverVatNumber: string): Promise<IntraCommunityResult> => {
  const response = await apiClient.get<ApiResponse<IntraCommunityResult>>(`${BASE_URL}/vat/check-intra-community`, {
    params: { senderVatNumber, receiverVatNumber }
  });
  return response.data;
};

// =============================================================================
// PEPPOL
// =============================================================================

export const checkPeppolRegistration = async (vatNumber: string): Promise<PeppolRegistration> => {
  const response = await apiClient.get<ApiResponse<PeppolRegistration>>(`${BASE_URL}/peppol/check/${vatNumber}`);
  return response.data;
};

export const refreshPeppolRegistration = async (vatNumber: string): Promise<PeppolRegistration> => {
  const response = await apiClient.post<ApiResponse<PeppolRegistration>>(`${BASE_URL}/peppol/refresh/${vatNumber}`);
  return response.data;
};

export const getPeppolCache = async (registered?: boolean, limit = 100): Promise<PeppolRegistration[]> => {
  const response = await apiClient.get<ApiResponse<PeppolRegistration[]>>(`${BASE_URL}/peppol/cache`, {
    params: { registered, limit }
  });
  return response.data;
};

export const clearExpiredPeppolCache = async (): Promise<{ message: string }> => {
  const response = await apiClient.delete<{ message: string }>(`${BASE_URL}/peppol/cache/expired`);
  return response;
};

export const getPeppolDeliveryStatus = async (documentId: string): Promise<unknown> => {
  const response = await apiClient.get<ApiResponse<unknown>>(`${BASE_URL}/peppol/delivery/${documentId}`);
  return response.data;
};

// =============================================================================
// FALCO
// =============================================================================

export const getFalcoDocumentStatus = async (documentId: string): Promise<unknown> => {
  const response = await apiClient.get<ApiResponse<unknown>>(`${BASE_URL}/falco/status/${documentId}`);
  return response.data;
};

export const getFalcoConfigStatus = async (): Promise<FalcoConfigStatus> => {
  const response = await apiClient.get<ApiResponse<FalcoConfigStatus>>(`${BASE_URL}/falco/config`);
  return response.data;
};

// =============================================================================
// DEVIS (QUOTES)
// =============================================================================

export const getDevisList = async (filters: DevisFilters = {}): Promise<DevisPaginatedResponse> => {
  const response = await apiClient.get<DevisPaginatedResponse>(`${BASE_URL}/devis`, { params: filters });

  const validated = validateResponse(
    response,
    DevisPaginatedResponseSchema,
    'GET /devis'
  );

  return validated;
};

export const getDevisById = async (id: number): Promise<Devis> => {
  const response = await apiClient.get<ApiResponse<Devis>>(`${BASE_URL}/devis/${id}`);

  const validated = validateResponse(
    response,
    ApiResponseSchema(DevisSchema),
    `GET /devis/${id}`
  );

  return validated.data;
};

export const downloadDevisPdf = async (id: number): Promise<Blob> => {
  const response = await apiClient.get<Blob>(`${BASE_URL}/devis/${id}/pdf`, {
    responseType: 'blob'
  });
  return response;
};

export const createDevis = async (data: CreateDevisData): Promise<Devis> => {
  const response = await apiClient.post<ApiResponse<Devis>>(`${BASE_URL}/devis`, data);

  const validated = validateResponse(
    response,
    ApiResponseSchema(DevisSchema),
    'POST /devis'
  );

  return validated.data;
};

export const updateDevis = async (id: number, data: Partial<CreateDevisData>): Promise<Devis> => {
  const response = await apiClient.put<ApiResponse<Devis>>(`${BASE_URL}/devis/${id}`, data);

  const validated = validateResponse(
    response,
    ApiResponseSchema(DevisSchema),
    `PUT /devis/${id}`
  );

  return validated.data;
};

export const deleteDevis = async (id: number): Promise<void> => {
  await apiClient.delete(`${BASE_URL}/devis/${id}`);
};

export const addDevisLine = async (devisId: number, data: AddDevisLineData): Promise<Devis> => {
  const response = await apiClient.post<ApiResponse<Devis>>(`${BASE_URL}/devis/${devisId}/lines`, data);

  const validated = validateResponse(
    response,
    ApiResponseSchema(DevisSchema),
    `POST /devis/${devisId}/lines`
  );

  return validated.data;
};

export const updateDevisLine = async (devisId: number, lineId: number, data: Partial<AddDevisLineData>): Promise<Devis> => {
  const response = await apiClient.put<ApiResponse<Devis>>(`${BASE_URL}/devis/${devisId}/lines/${lineId}`, data);

  const validated = validateResponse(
    response,
    ApiResponseSchema(DevisSchema),
    `PUT /devis/${devisId}/lines/${lineId}`
  );

  return validated.data;
};

export const removeDevisLine = async (devisId: number, lineId: number): Promise<Devis> => {
  const response = await apiClient.delete<ApiResponse<Devis>>(`${BASE_URL}/devis/${devisId}/lines/${lineId}`);

  const validated = validateResponse(
    response,
    ApiResponseSchema(DevisSchema),
    `DELETE /devis/${devisId}/lines/${lineId}`
  );

  return validated.data;
};

export const sendDevis = async (id: number): Promise<Devis> => {
  const response = await apiClient.post<ApiResponse<Devis>>(`${BASE_URL}/devis/${id}/send`);

  const validated = validateResponse(
    response,
    ApiResponseSchema(DevisSchema),
    `POST /devis/${id}/send`
  );

  return validated.data;
};

export const convertDevisToAffaire = async (id: number, affaireId: string): Promise<Devis> => {
  const response = await apiClient.post<ApiResponse<Devis>>(`${BASE_URL}/devis/${id}/convert`, { affaireId });

  const validated = validateResponse(
    response,
    ApiResponseSchema(DevisSchema),
    `POST /devis/${id}/convert`
  );

  return validated.data;
};

export const searchContacts = async (query: string, limit = 20): Promise<ContactSearchResult[]> => {
  const response = await apiClient.get<ApiResponse<ContactSearchResult[]>>(`${BASE_URL}/contacts/search`, {
    params: { q: query, limit }
  });
  return response.data;
};

interface NextDevisNumberResponse {
  nextNumber: string;
}

export const peekNextDevisNumber = async (year?: number): Promise<string> => {
  const response = await apiClient.get<ApiResponse<NextDevisNumberResponse>>(`${BASE_URL}/devis/next-number`, {
    params: year ? { year } : undefined
  });
  return response.data.nextNumber;
};

// =============================================================================
// BANK ACCOUNTS
// =============================================================================

export const getBankAccounts = async (): Promise<BankAccount[]> => {
  const response = await apiClient.get<ApiResponse<BankAccount[]>>(`${BASE_URL}/bank-accounts`);
  return response.data;
};

export const addBankAccount = async (data: {
  label: string;
  iban: string;
  bic: string;
  bankName?: string;
}): Promise<BankAccount> => {
  const response = await apiClient.post<ApiResponse<BankAccount>>(`${BASE_URL}/bank-accounts`, data);
  return response.data;
};
